import { QueryClient } from '@tanstack/react-query';
import { Cell, DocumentReferencesView, DocumentsService } from 'api';
import SheetAttributeManager from 'services/sheet-attribute-manager/SheetAttributeManager';
import { xlsxSpreadsheetNotesQueryOptions } from './xlsxSpreadsheetNotesQuery';

// Preflight returns a complete, document-qualified snapshot before the caller writes any cells.
export const prepareFinancialsMappingPush = async (queryClient: QueryClient, mapper: SheetAttributeManager) => {
  const requiredDocuments = new Set<number>();
  Object.values(mapper.sheetAttributeMapping.data).forEach(metrics => {
    Object.values(metrics).forEach(({ referenceData }) => {
      referenceData.forEach(reference => {
        if (Object.values(reference.sheetMappingConfig).some(sheet => sheet.mappedCells.length > 0)) {
          requiredDocuments.add(reference.documentReferenceId);
        }
      });
    });
  });

  const cellMapping: Record<string, Record<string, Cell>> = {};
  const documents = await Promise.all(
    [...requiredDocuments].map(async documentId => {
      let document: DocumentReferencesView | undefined;
      try {
        document = await queryClient.fetchQuery({
          queryKey: ['financialsMappingDocument', documentId],
          queryFn: () => DocumentsService.apiDocumentsReferencesRead(String(documentId)),
          staleTime: 0,
          retry: false,
        });
        if (document?.document_reference_id !== documentId || !document.file?.id) {
          throw new Error('Mapped document or file is unavailable.');
        }
        const notes = await queryClient.fetchQuery(xlsxSpreadsheetNotesQueryOptions(String(document.file.id)));
        notes.forEach(note => {
          if (note.name && note.content?.cells) {
            cellMapping[`${documentId}_${note.name}`] = note.content.cells;
          }
        });
        return document;
      } catch {
        // The document lookup can fail before its filename is available.
        throw new Error(
          `Could not load ${document?.file?.filename || `source ${documentId}`}. Reopen the source document and try again.`
        );
      }
    })
  );

  return {
    documents,
    evaluatedAttributeData: mapper.calculateValues(cellMapping, { requireCompleteSources: true }),
  };
};
